import React, { useEffect, useMemo, useState } from 'react';

const TYPE_ORDER = ['Weekly', 'Monthly', 'Quarterly', 'Yearly'];

const normalizeType = (value = '') => {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'weekly' || normalized === 'week') return 'Weekly';
  if (normalized === 'monthly' || normalized === 'month') return 'Monthly';
  if (normalized === 'quarterly' || normalized === 'quartley' || normalized === 'quarter') return 'Quarterly';
  if (normalized === 'yearly' || normalized === 'year') return 'Yearly';
  return null;
};

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function Milestones({
  actualRevenueByType = {},
  milestones = null,
  activeContracts = [],
  getSubscriptionPeriodAmount = () => 0,
  formatDateSafe = () => '',
}) {
  const [remoteMilestones, setRemoteMilestones] = useState([]);

  useEffect(() => {
    if (Array.isArray(milestones)) return;

    const fetchMilestones = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/milestones', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to fetch milestones');
        const data = await response.json();
        setRemoteMilestones(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error('Failed to fetch milestones:', error);
        setRemoteMilestones([]);
      }
    };

    fetchMilestones();
  }, [milestones]);

  const sourceMilestones = Array.isArray(milestones) ? milestones : remoteMilestones;

  const latestByType = useMemo(() => {
    const grouped = {};

    sourceMilestones.forEach((item) => {
      const normalizedType = normalizeType(item?.Type);
      if (!normalizedType) return;
      const current = grouped[normalizedType];
      const currentCreated = current?.CreatedAt ? new Date(current.CreatedAt) : new Date(0);
      const nextCreated = item?.CreatedAt ? new Date(item.CreatedAt) : new Date(0);
      if (!current || nextCreated > currentCreated) {
        grouped[normalizedType] = item;
      }
    });

    return grouped;
  }, [sourceMilestones]);

  const cards = TYPE_ORDER.map((type) => {
    const milestone = latestByType[type] || null;
    const target = safeNumber(milestone?.Revenue);
    const actual = safeNumber(actualRevenueByType[type]);
    const rawPercent = target > 0 ? (actual / target) * 100 : 0;
    const dialPercent = Math.max(0, Math.min(rawPercent, 100));

    return {
      type,
      target,
      actual,
      rawPercent,
      dialPercent,
    };
  });

  const normalizeSubscription = (value = '') => {
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'weekly' || normalized === 'week') return 'Weekly';
    if (normalized === 'monthly' || normalized === 'month') return 'Monthly';
    if (normalized === 'annual' || normalized === 'yearly' || normalized === 'year') return 'Annual';
    return value || '-';
  };

  const contractRows = Array.isArray(activeContracts) ? activeContracts : [];
  const formatDate = formatDateSafe;
  const getPeriodAmount = getSubscriptionPeriodAmount;

  const toValidDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const sortedContractRows = useMemo(() => {
    return [...contractRows].sort((a, b) => {
      const aEnd = toValidDate(a?.ContractEnd);
      const bEnd = toValidDate(b?.ContractEnd);

      if (aEnd && bEnd) {
        const diff = aEnd.getTime() - bEnd.getTime();
        if (diff !== 0) return diff;
      } else if (aEnd && !bEnd) {
        return -1;
      } else if (!aEnd && bEnd) {
        return 1;
      }

      const aStart = toValidDate(a?.ContractStart);
      const bStart = toValidDate(b?.ContractStart);
      if (aStart && bStart) {
        const diff = aStart.getTime() - bStart.getTime();
        if (diff !== 0) return diff;
      } else if (aStart && !bStart) {
        return -1;
      } else if (!aStart && bStart) {
        return 1;
      }

      const aName = String(a?.ClientName || a?.ContactName || '').toLowerCase();
      const bName = String(b?.ClientName || b?.ContactName || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [contractRows]);

  const subscriptionTotals = contractRows.reduce(
    (acc, contract) => {
      acc.weekly += getPeriodAmount(contract?.Amount, contract?.Subscription, 'weekly');
      acc.monthly += getPeriodAmount(contract?.Amount, contract?.Subscription, 'monthly');
      acc.annual += getPeriodAmount(contract?.Amount, contract?.Subscription, 'yearly');
      return acc;
    },
    { weekly: 0, monthly: 0, annual: 0 }
  );

  return (
    <div className="mt-4 rounded-2xl border border-white/35 bg-gradient-to-br from-cyan-100/70 via-indigo-100/70 to-emerald-100/60 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.2)] backdrop-blur-xl">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-semibold text-indigo-900/90">Milestones</h3>
        <p className="text-xs text-indigo-800/75">Progress to current target</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((item) => (
          <div
            key={item.type}
            className="rounded-xl border border-white/45 bg-white/70 p-3 shadow-[0_8px_20px_rgba(15,23,42,0.15)]"
          >
            <p className="text-sm font-semibold text-indigo-900">{item.type}</p>
            <div className="mt-2 flex items-center gap-3">
              <div
                className="relative h-16 w-16 rounded-full"
                style={{
                  background: `conic-gradient(#10b981 ${item.dialPercent}%, #e2e8f0 ${item.dialPercent}% 100%)`,
                }}
              >
                <div className="absolute inset-2 flex items-center justify-center rounded-full bg-white text-xs font-bold text-indigo-900">
                  {item.rawPercent.toFixed(0)}%
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-indigo-800/80">Actual: £{item.actual.toFixed(2)}</p>
                <p className="text-xs text-indigo-800/80">Target: £{item.target.toFixed(2)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedContractRows.length > 0 && (
        <div className="mt-4 rounded-xl border border-white/45 bg-white/75 p-3 shadow-[0_8px_20px_rgba(15,23,42,0.12)]">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-indigo-900">Active Contract Subscriptions</p>
            <p className="text-xs text-indigo-800/75">{sortedContractRows.length} active</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="border-b border-indigo-100 text-indigo-900/80">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold">Client</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Subscription</th>
                  <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Contract Period</th>
                </tr>
              </thead>
              <tbody>
                {sortedContractRows.map((contract) => (
                  <tr key={contract?.id || `${contract?.ClientName || contract?.ContactName}-${contract?.ContractStart || ''}`} className="border-b border-indigo-50">
                    <td className="px-2 py-1.5 text-indigo-900">{contract?.ClientName || contract?.ContactName || '-'}</td>
                    <td className="px-2 py-1.5 text-indigo-900">{normalizeSubscription(contract?.Subscription)}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700">£{safeNumber(contract?.Amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-1.5 text-indigo-800/80">
                      {(formatDate(contract?.ContractStart) || '-') + ' -> ' + (formatDate(contract?.ContractEnd) || 'Ongoing')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-indigo-900 sm:grid-cols-3">
            <p>Weekly Equivalent: <span className="font-semibold text-emerald-700">£{subscriptionTotals.weekly.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
            <p>Monthly Equivalent: <span className="font-semibold text-emerald-700">£{subscriptionTotals.monthly.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
            <p>Annual Equivalent: <span className="font-semibold text-emerald-700">£{subscriptionTotals.annual.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Milestones;
